import { Component, OnDestroy } from '@angular/core';
import { AbstractControlOptions, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../../auth/auth.service';
import { UserDto } from '../../../../auth/user.dto';
import { CommunityActionDto } from '../../../../community-action/dto/communityAction.dto';
import { CourseDto } from '../../../../community-action/dto/course.dto';
import { SessionDto } from '../../../../community-action/dto/session.dto';
import { ConfirmationComponent } from '../../../../shared/confirmation/confirmation.component';
import { DisplayCommunityAction } from '../../../../shared/models/displayCommunityAction';
import { DisplayLabel } from '../../../../shared/models/displayLabel';
import { NatureTypeEnum } from '../../../../shared/models/enums/nature-type.enum';
import { SessionStatus } from '../../../../shared/models/enums/session-status.enum';
import { StageState, labelsByStageState } from '../../../../shared/models/enums/stage-state.enum';
import { BeneficiariesService } from '../../../../shared/services/beneficiaries.service';
import { CommunityActionService } from '../../../../shared/services/community-action.service';
import { DateService } from '../../../../shared/services/date.service';
import { SessionService } from '../../../../shared/services/session.service';
import { StageNatureTypeService } from '../../../../shared/services/stageNatureType.service';
import { StagesService } from '../../../../shared/services/stages.service';
import { StringUtils } from '../../../../shared/services/stringUtils.service';
import { ToastService } from '../../../../shared/toast/toast.service';
import { Utils } from '../../../../shared/utils';
import { DateValidators } from '../../../../shared/validators/date.validators';
import { StageSaveDto } from '../../../../stages/stageSaveDto';
import { BeneficiaryDto } from '../../../dto/beneficiary.dto';
import { FseDetailInitialData, FseDetailsComponent } from '../../fse/fse-details/fse-details.component';
import { DisplayStageNatureTypeDto } from '../display-stage-nature-type.dto';
import { Stage } from '../stage';

@Component({
  selector: 'app-stage-details',
  templateUrl: './stage-details.component.html',
})
export class StageDetailsComponent implements OnDestroy {
  stringAscOrder = StringUtils.stringAscOrder;
  $stageState = Utils.castTo<StageState>();

  public stage: Stage;
  public stageDetailsForm: UntypedFormGroup;
  public stageNatureTypes: DisplayStageNatureTypeDto[];
  public isModificationMode: boolean = false;
  public submitted: boolean = false;
  public beneficiary: BeneficiaryDto;
  public stageNatureTypesList: DisplayLabel[] = [];
  public isNatureFse: boolean = false;
  public hasStructure: boolean = true;
  public canGenerate: boolean = false;

  public user: UserDto | undefined = undefined;
  private readonly destroy$: Subject<void> = new Subject();

  public readonly StringUtils: StringUtils = StringUtils;
  public readonly stageStates: Map<StageState, string> = labelsByStageState;

  private natureWithoutType: string[] = [];
  private natureFse: string[] = [];
  public labels: DisplayCommunityAction[] = [];
  public sessions: DisplayLabel[] = [];
  public session: SessionDto;
  public action: DisplayCommunityAction;
  private accessMode: string;
  public hiddenStates: StageState[] = [];

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly modal: NgbActiveModal,
    private readonly stagesService: StagesService,
    private readonly stageNatureTypeService: StageNatureTypeService,
    private readonly toastService: ToastService,
    private readonly beneficiaryService: BeneficiariesService,
    private readonly modalService: NgbModal,
    private readonly communityActionService: CommunityActionService,
    private readonly sessionService: SessionService,
    private readonly authService: AuthService,
    protected readonly dateService: DateService,
  ) {
    this.beneficiaryService.accessMode.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((accessMode: string) => (this.accessMode = accessMode));
  }

  public async onInit(beneficiaryId: number, stageId?: number, communityAction?: CommunityActionDto): Promise<void> {
    this.user = await this.authService.getUser();

    this.stageNatureTypes = await this.stageNatureTypeService.findAll();
    this.stageNatureTypes = this.stageNatureTypes.filter(
      (entity) => ![String(NatureTypeEnum.FSEFunded), String(NatureTypeEnum.CatalogActions)].includes(entity.nature),
    );

    this.natureWithoutType = Array.from(
      new Set<string>(
        this.stageNatureTypes
          .filter((item: DisplayStageNatureTypeDto) => !Utils.isNotEmpty(item.type))
          .map((item: DisplayStageNatureTypeDto) => item.nature),
      ),
    );

    this.natureFse = Array.from(
      new Set<string>(
        this.stageNatureTypes
          .filter((item: DisplayStageNatureTypeDto) =>
            [NatureTypeEnum.ActionOnPrescription, NatureTypeEnum.CommunityWorkshop, NatureTypeEnum.CommunitySupport].includes(item.category),
          )
          .map((item: DisplayStageNatureTypeDto) => item.nature),
      ),
    );

    this.beneficiary = await this.beneficiaryService.findById(beneficiaryId);
    await this.loadForm(stageId, communityAction);
    if (!this.beneficiary.orientation.beginDate) {
      this.toastService.show("Le bénéficiaire ne possède pas de date de début d'orientation, impossible de lui créer une étape.", 'warning');
    }
    if (!this.beneficiary.orientation.structure) {
      this.hasStructure = false;
      this.toastService.show('Le bénéficiaire ne possède pas de structure, impossible de lui créer ni modifier une étape.', 'warning');
    }

    this.stageDetailsForm.controls.stageType.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: number) => this.handleTypeChange(value));

    this.stageDetailsForm.controls.stageNature.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: string) => this.handleNatureChange(value));

    this.stageDetailsForm.controls.session.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: number) => this.handleSessionChange(value));
  }

  private async loadForm(stageId?: number, communityAction?: CommunityActionDto): Promise<void> {
    this.hiddenStates = [];
    let natureValue: DisplayStageNatureTypeDto = undefined;
    this.stage = stageId ? await this.stagesService.findByStageId(stageId) : Stage.undefinedStage();
    if (stageId) {
      natureValue = this.stageNatureTypes.find((item: DisplayStageNatureTypeDto) => item.id === this.stage.stageNatureTypeId);
    }

    if (communityAction) {
      natureValue = this.stageNatureTypes.find((item: DisplayStageNatureTypeDto) => item.id === communityAction.natureTypeId);
      this.stage.communityActionId = communityAction.id;
      this.stage.stageNatureTypeId = natureValue.id;
      this.stage.organization = communityAction.structure.label;
    }

    this.canGenerate = stageId ? natureValue.nature === NatureTypeEnum.ActionOnPrescription : false;
    this.isNatureFse = natureValue && this.natureFse.includes(natureValue?.nature);

    let hasExistingStageWithActiveSession: boolean =
      this.stage.id && this.isNatureFse && ((this.stage.sessionId || !this.user.groups.includes('ADMIN')) as boolean);

    let hasActiveSessionWithNatureFse: boolean = this.isNatureFse && this.stage.sessionId ? true : false;

    this.stageDetailsForm = this.formBuilder.group(
      {
        beginDate: [
          { value: this.stage.beginDate, disabled: hasActiveSessionWithNatureFse },
          [Validators.required, DateValidators.dateFormatValidator],
        ],
        endDate: [{ value: this.stage.endDate, disabled: hasActiveSessionWithNatureFse }, [Validators.required, DateValidators.dateFormatValidator]],
        stageNature: [
          {
            value: natureValue ? natureValue.nature : natureValue,
            disabled: hasExistingStageWithActiveSession || communityAction,
          },
          Validators.required,
        ],
        stageType: [
          {
            value: this.stage.stageNatureTypeId,
            disabled: hasExistingStageWithActiveSession || !stageId,
          },
          Validators.required,
        ],
        label: [
          {
            value: this.stage.communityActionId ? this.stage.communityActionId : this.stage.label,
            disabled: hasActiveSessionWithNatureFse || communityAction,
          },
        ],
        session: [
          {
            value: this.stage.sessionId,
            disabled: this.sessions.length === 0 || hasActiveSessionWithNatureFse,
          },
        ],
        stageState: [
          {
            value: this.stage.stageState,
            disabled: hasActiveSessionWithNatureFse && this.stage.beginDate <= this.dateService.getCurrent(),
          },
          Validators.required,
        ],
        duration: [{ value: this.stage.duration, disabled: hasActiveSessionWithNatureFse }, Validators.pattern(Utils.placesNumberPattern)],
        organization: [
          {
            value: this.stage.organization,
            disabled: hasActiveSessionWithNatureFse || this.stage.communityActionId || this.stage.sessionId,
          },
        ],
        comment: [this.stage.comment],
        orientationBeginDate: [this.beneficiary.orientation.beginDate, Validators.required],
      },
      this.formControlOptions(),
    );

    if (this.isNatureFse) {
      this.labels = await this.communityActionService.findActionsLabelStage(natureValue.id);
      const filteredAction: DisplayCommunityAction[] = this.labels.filter((item: DisplayCommunityAction) => this.stage.communityActionId === item.id);
      if (filteredAction.length === 0) {
        const stageAction: DisplayCommunityAction = await this.communityActionService.findActionsLabelStageById(this.stage.communityActionId);
        if (stageAction) {
          this.labels = [...this.labels, stageAction];
        }
      }
      this.action = this.labels.find((label: DisplayCommunityAction) => label.id === this.stage.communityActionId);
      if (this.stage.communityActionId && this.stage.sessionId) {
        this.findSessionsLabel();
      }
      if (this.stage.sessionId) {
        if (this.stageDetailsForm.controls?.beginDate.value > this.dateService.getCurrent()) {
          this.hiddenStates = [StageState.InProgress, StageState.Conducted, StageState.Interrupted, StageState.RefusedOrganization];
        }
      } else if (hasExistingStageWithActiveSession) {
        this.stageDetailsForm.controls.label.disable();
      }
    }

    this.isModificationMode = !!stageId;
  }

  private async handleNatureChange(nature: string): Promise<void> {
    this.resetFormAtNatureChange();
    if (this.natureFse.includes(nature)) {
      this.isNatureFse = true;
      this.stageDetailsForm.controls.label.disable();
      this.stageDetailsForm.controls.organization.disable();
      this.stageDetailsForm.controls.session.disable();
      this.stageDetailsForm.controls.beginDate.disable();
      this.stageDetailsForm.controls.endDate.disable();
      this.stageDetailsForm.controls.duration.disable();
      this.stageDetailsForm.controls.stageState.disable();
      this.setFseValidator();
    } else if (this.natureWithoutType.includes(nature)) {
      this.stageDetailsForm.controls.stageType.disable();
    } else {
      this.stageDetailsForm.controls.session.disable();
      this.stageDetailsForm.controls.stageType.setValidators([Validators.required]);
    }
    this.canGenerate = nature === NatureTypeEnum.ActionOnPrescription;
    this.stageDetailsForm.updateValueAndValidity();
  }

  private async handleTypeChange(natureTypeId: number): Promise<void> {
    if (natureTypeId) {
      this.resetFormAtNatureTypeChange();
      if (this.isNatureFse) {
        this.stageDetailsForm.controls.session.disable();
        this.stageDetailsForm.controls.label.setValidators([Validators.required]);
        this.labels = await this.communityActionService.findActionsLabelStage(natureTypeId);
        if (this.user?.groups?.includes('STRUCTURE')) {
          this.labels = this.labels.filter((label: DisplayCommunityAction) => {
            return (
              ([NatureTypeEnum.CommunityWorkshop, NatureTypeEnum.CommunitySupport].includes(label.nature as NatureTypeEnum) &&
                (label.structure.label === this.user.structureLabel || label.openAction)) ||
              label.nature === NatureTypeEnum.ActionOnPrescription
            );
          });
        }
      }
      this.stageDetailsForm.updateValueAndValidity();
    }
  }

  public async handleLabelChange(action: any): Promise<void> {
    if (this.isNatureFse && action) {
      this.action = action;
      this.sessions = await this.sessionService.findSessionsLabel({ actionId: action.id, statuses: [SessionStatus.Active] });
      if (!action.withSession) {
        this.stageDetailsForm.controls.session.disable();
        this.stageDetailsForm.controls.session.setValue(null, { emitEvent: false });
        this.stageDetailsForm.controls.organization.setValue(this.action.structure.label, { emitEvent: false });
        this.stageDetailsForm.controls.beginDate.enable();
        this.stageDetailsForm.controls.endDate.enable();
        this.stageDetailsForm.controls.duration.enable();
        this.stageDetailsForm.controls.stageState.enable();
        this.stageDetailsForm.controls.beginDate.setValue(null, { emitEvent: false });
        this.stageDetailsForm.controls.endDate.setValue(null, { emitEvent: false });
        this.stageDetailsForm.controls.duration.setValue(null, { emitEvent: false });
        this.stageDetailsForm.controls.stageState.setValue(null, { emitEvent: false });
      } else {
        if (this.sessions.length !== 0) {
          this.stageDetailsForm.controls.session.enable();
          this.stageDetailsForm.controls.session.setValidators([Validators.required]);
        } else {
          this.toastService.show("Aucune session n'est disponible pour cette action.", 'warning');
        }
        this.stageDetailsForm.controls.session.enable();
        this.stageDetailsForm.controls.session.setValidators([Validators.required]);
        this.stageDetailsForm.controls.organization.disable();
        this.stageDetailsForm.controls.beginDate.disable();
        this.stageDetailsForm.controls.endDate.disable();
        this.stageDetailsForm.controls.duration.disable();
        this.stageDetailsForm.controls.stageState.disable();
        this.stageDetailsForm.controls.organization.setValue(null, { emitEvent: false });
        this.stageDetailsForm.controls.beginDate.setValue(null, { emitEvent: false });
        this.stageDetailsForm.controls.endDate.setValue(null, { emitEvent: false });
        this.stageDetailsForm.controls.stageState.setValue(null, { emitEvent: false });
        this.stageDetailsForm.controls.duration.setValue(null, { emitEvent: false });
      }
      this.stageDetailsForm.updateValueAndValidity();
    }
  }

  public async handleSessionChange(sessionId: number): Promise<void> {
    if (sessionId) {
      this.session = await this.sessionService.findSessionById(sessionId);
      this.sortCourses();
      this.stageDetailsForm.controls.beginDate.setValue(new Date(this.session.courses[0].day), {
        emitEvent: false,
      });
      this.stageDetailsForm.controls.endDate.setValue(new Date(this.session.courses[this.session.courses.length - 1].day), {
        emitEvent: false,
      });
      this.stageDetailsForm.controls.duration.setValue(this.computeDuration(), { emitEvent: false });
      this.stageDetailsForm.controls.stageState.setValue(moment().isSameOrBefore(this.session.courses[0].day) ? 'Planned' : 'Conducted', {
        emitEvent: false,
      });
      this.stageDetailsForm.controls.organization.setValue(this.action.structure.label, { emitEvent: false });
    }
  }

  private sortCourses(): void {
    this.session.courses = [...this.session.courses].sort((a: CourseDto, b: CourseDto) => (moment(a.day).isBefore(b.day) ? -1 : 1));
  }

  private computeDuration(): number {
    let duration: number = 0;
    for (const course of this.session.courses) {
      duration += 60 * (course.end.hour - course.start.hour) + (course.end.minute - course.start.minute);
    }
    return duration;
  }

  private resetFormAtNatureChange(): void {
    this.isNatureFse = false;
    this.stageDetailsForm.controls.stageType.enable();
    this.stageDetailsForm.controls.stageType.setValue(null, { emitEvent: false });
    this.stageDetailsForm.controls.organization.enable();
    this.stageDetailsForm.controls.organization.setValue(null, { emitEvent: false });
    this.stageDetailsForm.controls.label.enable();
    this.stageDetailsForm.controls.label.setValue(null, { emitEvent: false });
    this.stageDetailsForm.controls.session.enable();
    this.stageDetailsForm.controls.session.setValue(null, { emitEvent: false });
    this.stageDetailsForm.controls.beginDate.enable();
    this.stageDetailsForm.controls.beginDate.setValue(null, { emitEvent: false });
    this.stageDetailsForm.controls.endDate.enable();
    this.stageDetailsForm.controls.endDate.setValue(null, { emitEvent: false });
    this.stageDetailsForm.controls.duration.enable();
    this.stageDetailsForm.controls.duration.setValue(null, { emitEvent: false });
    this.stageDetailsForm.controls.stageState.enable();
    this.stageDetailsForm.controls.stageState.setValue(null, { emitEvent: false });
  }

  private resetFormAtNatureTypeChange(): void {
    this.stageDetailsForm.controls.label.enable();
    this.stageDetailsForm.controls.label.setValue(null, { emitEvent: false });
    this.stageDetailsForm.controls.organization.setValue(null, { emitEvent: false });
    this.stageDetailsForm.controls.session.setValue(null, { emitEvent: false });
  }

  private setFseValidator(): void {
    this.stageDetailsForm.controls.label.setValidators([Validators.required]);
    this.stageDetailsForm.controls.stageType.setValidators([Validators.required]);
    this.stageDetailsForm.controls.session.setValidators([Validators.required]);
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public cancel(): void {
    this.stageDetailsForm.reset();
    this.modal.close();
  }

  public async onSubmit(): Promise<void> {
    this.submitted = true;
    if (this.stageDetailsForm.valid) {
      await this.createUpdateStage();
    } else {
      this.toastService.show('La sauvegarde a échoué, veuillez vérifier les données.', 'danger');
    }
  }

  public async generateSurvey(): Promise<void> {
    this.submitted = true;
    if (this.stageDetailsForm.valid) {
      await this.createUpdateStage();
      const structureId: number = await this.communityActionService.findStructureIdByAction(this.action.id);
      const modal: NgbModalRef = this.modalService.open(FseDetailsComponent, {
        windowClass: 'rightModal',
        size: 'lg',
        backdrop: 'static',
      });
      const modalComponent: FseDetailsComponent = modal.componentInstance as FseDetailsComponent;
      await modalComponent.onInit(undefined, {
        year: this.stageDetailsForm.controls.beginDate.value.getFullYear().toString(),
        structureId: structureId,
        labelId: this.action.id,
        beginDate: this.stageDetailsForm.controls.beginDate.value,
        setExitDate: true,
      } as FseDetailInitialData);
      this.modal.close();
    }
  }

  private async createUpdateStage(): Promise<void> {
    const stage: StageSaveDto = new StageSaveDto(
      this.stageDetailsForm.controls.beginDate.value,
      this.stageDetailsForm.controls.endDate.value,
      this.stageDetailsForm.controls.stageType.value,
      this.stageDetailsForm.controls.stageState.value,
      this.beneficiary.id,
      Utils.isNumber(this.stageDetailsForm.controls.label.value)
        ? (this.labels.find((item: DisplayCommunityAction) => item.id === this.stageDetailsForm.controls.label.value) || { label: undefined }).label
        : this.stageDetailsForm.controls.label.value,
      this.stageDetailsForm.controls.organization.value,
      this.stageDetailsForm.controls.comment.value,
      Utils.isNumber(this.stageDetailsForm.controls.label.value) ? this.stageDetailsForm.controls.label.value : null,
      Utils.isNumber(this.stageDetailsForm.controls.session.value) ? this.stageDetailsForm.controls.session.value : null,
      this.stageDetailsForm.controls.duration.value,
    );

    try {
      if (this.stage?.id) {
        await this.stagesService.updateStage(this.stage.id, stage, this.accessMode);
        this.toastService.show("L'étape a été modifiée avec succès.", 'success');
        this.modal.close();
      } else {
        await this.stagesService.saveStage(stage, this.accessMode);
        this.toastService.show("L'étape a été ajoutée avec succès.", 'success');
        this.modal.close();
      }
    } catch (error) {
      if (error.status === 440) {
        this.toastService.show('Une étape existe déjà pour la session choisie.', 'danger');
      } else if (error.status === 442) {
        this.toastService.show("Impossible de créer, la date de création ne correspond pas à la période d'orientation passée.", 'danger');
      } else if (error.status === 443) {
        this.toastService.show("Impossible de créer l'étape, le bénéficiaire n'a pas de référent.", 'danger');
      } else if (error.status === 444) {
        this.toastService.show("Impossible de créer l'étape, aucune orientation pour le bénéficiaire au début de l'étape.", 'danger');
      }
    }
  }

  public async deleteStage(): Promise<void> {
    const confirm: boolean = await this.openConfirmationModal();
    if (confirm) {
      await this.stagesService.deleteStage(this.stage.id);
      this.toastService.show("L'étape a été supprimée avec succès.", 'success');
      this.modal.close();
    }
  }

  private async openConfirmationModal(): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(ConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: ConfirmationComponent = modal.componentInstance as ConfirmationComponent;
    return await modalComponent.confirm('Êtes-vous sûr(e) de vouloir supprimer cette étape ?');
  }

  private async findSessionsLabel(): Promise<void> {
    this.sessions = await this.sessionService.findSessionsLabel({ actionId: this.stage.communityActionId, statuses: [SessionStatus.Active] });
    const filteredSession: DisplayLabel[] = this.sessions.filter((item: DisplayLabel) => this.stage.sessionId === item.id);
    if (filteredSession.length === 0) {
      const stageSession: DisplayLabel = await this.communityActionService.findSessionLabelById(this.stage.sessionId);
      if (stageSession) {
        this.sessions.push(stageSession);
      }
    }
  }

  private formControlOptions(): AbstractControlOptions {
    return this.accessMode === 'retroactive' || this.user.groups.includes('ADMIN')
      ? {
          validators: [DateValidators.datesOrderValidator('beginDate', 'endDate')],
        }
      : {
          validators: [
            DateValidators.datesOrderValidator('beginDate', 'endDate'),
            DateValidators.datesOrderValidatorValue('beginDate', this.beneficiary.orientation.beginDate),
          ],
        };
  }
}
